import { useCallback, useEffect, useRef } from "react"
import Api from "@ciq/shared/api/api"
import { useAppContext } from "@ciq/shared/hooks/context"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { buildMovePayload } from "@ciq/shared/pages/file_upload/helpers"

/**
 * @typedef {Object} UseMoveFilesResult
 * @property {(targetBuildingId: string|null, fileIds: string[]) => Promise<void>} moveFiles - Move files to a target building by explicit file IDs
 */

/**
 * Standalone hook for moving files to a building by explicit file IDs.
 * Used by drag-and-drop from sidebar (where IDs come from dataTransfer).
 * Delegates to buildMovePayload for placement-clearing business logic.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @param {Array} files - Current file list for placement status lookup
 * @param {Function} onFilesChanged - Callback to trigger parent file re-fetch
 * @param {Array} [buildings] - Building items with nested floors for hierarchy resolution
 * @returns {UseMoveFilesResult} Hook result with moveFiles action
 */
export function useMoveFiles(schoolId, files, onFilesChanged, buildings) {
  const { addLoadingId, removeLoadingId } = useAppContext()
  const mountedRef = useRef(true)
  const controllersRef = useRef(new Set())
  useEffect(() => {
    const controllers = controllersRef.current
    return () => {
      mountedRef.current = false
      controllers.forEach(c => c.abort())
    }
  }, [])

  const moveFiles = useCallback(async (targetBuildingId, fileIds) => {
    if (!fileIds || fileIds.length === 0) return

    const loadingId = "file_upload_drag_move"
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      const fileUpdates = fileIds.map(id => {
        const file = files.find(f => f.id === id)
        return file ? buildMovePayload(file, targetBuildingId, buildings) : { id, building_zone_id: targetBuildingId === "inbox" ? null : targetBuildingId }
      })

      await Api.put(`schools/${schoolId}/uploaded-files/bulk`, { files: fileUpdates }, { signal: controller.signal })
      if (mountedRef.current) onFilesChanged()
    } catch (e) {
      if (e.handled) return
      if (mountedRef.current) ErrorToast("Unable to move files")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, files, onFilesChanged, buildings, addLoadingId, removeLoadingId])

  return { moveFiles }
}
